<template>
  <div>
    <!-- Phone Input -->
    <div class="col form-group">
      <input
        v-if="!useInternationalPhone"
        ref="phoneInput"
        id="phone"
        v-model="obj.phone"
        v-mask="'(##) #####-####'"
        class="w-100"
        :class="template.secondaryTheme ? 'secondary-input-theme' : ' c-input'"
        :placeholder="$t('phone.mobile')"
        type="tel"
        @keyup.enter="validateFields"
      />

      <!-- Phone Estrangeiro -->
      <div v-else class="phoneNumber">
        <div
          v-if="international"
          class="ddi-picker"
          :class="template.secondaryTheme ? 'ddi-picker--dark' : 'ddi-picker--light'"
          ref="ddiPicker"
        >
          <button
            type="button"
            class="ddi-picker-trigger c-input"
            :class="template.secondaryTheme ? 'secondary-input-theme' : ''"
            @click.stop="toggleDdiDropdown"
          >
            <span class="ddi-picker-value">{{ selectedDdiLabel }}</span>
            <span class="ddi-picker-arrow" :class="{ open: ddiDropdownOpen }">▼</span>
          </button>
          <ul
            v-show="ddiDropdownOpen"
            class="ddi-picker-list"
            :class="template.secondaryTheme ? 'ddi-picker-list--dark' : 'ddi-picker-list--light'"
          >
            <li v-if="loadingDdiCountries" class="ddi-picker-loading">
              {{ $t('phone.loadingCountries') }}
            </li>
            <li
              v-for="country in ddiCountries"
              :key="`${country.iso3 || country.id}-${country.phone_Code}`"
              :class="{ selected: selectedPhoneCode === '+' + country.phone_Code }"
              @click="selectDdiCountry(country)"
            >
              {{ country.country_Name }} +{{ country.phone_Code }}
            </li>
          </ul>
        </div>

        <div v-else>
          <input
            list="countries"
            class="c-input inputWidth"
            :placeholder="$t('phone.ddiExample')"
            id="phoneCodeInput"
            v-model="selectedPhoneCode"
            @keyup.enter="validateFields"
          />
          <datalist id="countries">
            <option
              class="text-black"
              v-for="(i, index) in ddiCountries"
              :value="'+' + i.phone_Code"
              :key="`${i.phone_Code}-${index}`"
            >
              {{ i.country_Name }}
            </option>
          </datalist>
        </div>

        <input
          v-model="phoneNumber"
          class="w-100"
          :class="template.secondaryTheme ? 'secondary-input-theme' : ' c-input'"
          :placeholder="$t('phone.number')"
          @keyup.enter="validateFields"
        />
      </div>
    </div>
  </div>
</template>

<script>
/* State */
import { mapState, mapMutations, mapActions } from 'vuex'

/* Helpers */
import { mask } from 'vue-the-mask'
import {
  getDdiCountriesFromApi,
  findCountryByIso3,
  findCountryByPhoneCode,
  findCountryByPhonePrefix,
  getCountryIso3,
  isFrancePhoneCountry,
} from '@/helpers/popularPhoneCountries'

/* Services */
import { loginFB } from '../../services/ServFB.js'

const lFB = new loginFB()

export default {
  name: 'Phone',
  mask: {
    ...mask,
    tokens: {
      ...mask.tokens,
      null: /./,
    },
  },
  props: {
    validate: {
      Boolean,
      required: true,
    },
    international: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    obj: {},
    selectedPhoneCode: '',
    phoneNumber: '',
    toastMessage: '',
    ddiDropdownOpen: false,
    loadingDdiCountries: false,
  }),
  computed: {
    ...mapState(['config', 'registrationObject', 'subdomain', 'template', 'countries']),
    useInternationalPhone() {
      if (this.international || this.obj.passport) return true
      const iso3 = String(this.config.iso3 || '').toUpperCase()
      return iso3 !== '' && iso3 !== 'BRA'
    },
    phone() {
      if (!this.useInternationalPhone) return this.obj.phone
      return this.formatInternationalPhoneNumber()
    },
    ddiCountries() {
      if (!this.useInternationalPhone) return []

      if (this.international) {
        return getDdiCountriesFromApi(
          this.countries,
          String(this.config.iso3 || '').toUpperCase(),
        )
      }

      return getDdiCountriesFromApi(this.countries)
    },
    selectedDdiCountry() {
      if (!this.selectedPhoneCode) return null

      return (
        this.ddiCountries.find(
          country => this.selectedPhoneCode === '+' + country.phone_Code,
        ) || null
      )
    },
    selectedDdiLabel() {
      if (this.selectedDdiCountry) {
        return this.selectedDdiCountry.phone_Code
      }

      return String(this.selectedPhoneCode || '').replace(/\D/g, '') || '+'
    },
  },
  mounted() {
    // Create a deep copy of the object in the store
    this.obj = JSON.parse(JSON.stringify(this.registrationObject))
    if (this.useInternationalPhone) {
      this.parseExistingInternationalPhone()
      this.loadCountriesIfNeeded()
    }

    if (this.international) {
      document.addEventListener('click', this.handleDdiClickOutside)
    }
  },
  beforeDestroy() {
    if (this.international) {
      document.removeEventListener('click', this.handleDdiClickOutside)
    }
  },
  methods: {
    ...mapMutations([
      'addRegistrationError',
      'clearRegistrationErrors',
      'updateRegistrationObject',
    ]),
    ...mapActions(['setCountries']),
    toggleDdiDropdown() {
      this.ddiDropdownOpen = !this.ddiDropdownOpen
    },
    selectDdiCountry(country) {
      this.selectedPhoneCode = '+' + country.phone_Code
      this.applyPhoneCountry(country)
      this.persistPhoneCountry()
      this.ddiDropdownOpen = false
    },
    handleDdiClickOutside(event) {
      const picker = this.$refs.ddiPicker
      if (picker && !picker.contains(event.target)) {
        this.ddiDropdownOpen = false
      }
    },
    parseExistingInternationalPhone() {
      if (!this.obj.phone) return

      const rawPhone = String(this.obj.phone).trim()
      const match = rawPhone.match(/^(\+\d+)\s*(.*)$/)

      if (match) {
        this.selectedPhoneCode = match[1]
        this.phoneNumber = match[2].replace(/\D/g, '')
        this.syncPhoneCountryFromSelection()
        return
      }

      const digits = rawPhone.replace(/\D/g, '')
      if (!digits) return

      const country = findCountryByPhonePrefix(this.ddiCountries, digits)
      if (country) {
        this.selectedPhoneCode = '+' + country.phone_Code
        this.phoneNumber = digits.slice(String(country.phone_Code).length)
        this.applyPhoneCountry(country)
        this.persistPhoneCountry()
        return
      }

      this.phoneNumber = digits
      this.syncPhoneCountryFromSelection()
    },
    loadCountriesIfNeeded() {
      if (this.countries.length) {
        this.initInternationalPhoneDefaults()
        return
      }

      this.loadingDdiCountries = true

      lFB
        .GetCountries()
        .then(data => {
          this.setCountries(data)
          this.initInternationalPhoneDefaults()
        })
        .catch(() => {})
        .finally(() => {
          this.loadingDdiCountries = false
        })
    },
    initInternationalPhoneDefaults() {
      const iso3 = String(this.config.iso3 || '').toUpperCase()
      const list = this.ddiCountries.length ? this.ddiCountries : this.countries

      if (!this.selectedPhoneCode && iso3) {
        const country = findCountryByIso3(list, iso3)
        if (country && country.phone_Code) {
          this.selectedPhoneCode = '+' + country.phone_Code
          this.applyPhoneCountry(country)
          this.persistPhoneCountry()
        }
        return
      }

      this.syncPhoneCountryFromSelection()
      this.persistPhoneCountry()
    },
    applyPhoneCountry(country) {
      if (!country) return

      this.obj.phoneCountryIso3 = getCountryIso3(country)
      this.obj.phoneCountryName = country.country_Name || country.country_Name_En || ''
      this.obj.phoneCountryCode = country.phone_Code || ''
      this.obj.phoneCountryId = country.id || null
      this.obj.phoneCountryIsFrance = isFrancePhoneCountry(country)
    },
    persistPhoneCountry() {
      this.syncPhoneCountryFromSelection()

      this.$store.commit('updateRegistrationObject', {
        phoneCountryIso3: this.obj.phoneCountryIso3 || '',
        phoneCountryName: this.obj.phoneCountryName || '',
        phoneCountryCode: this.obj.phoneCountryCode || '',
        phoneCountryId: this.obj.phoneCountryId || null,
        phoneCountryIsFrance: Boolean(this.obj.phoneCountryIsFrance),
      })
    },
    syncPhoneCountryFromSelection() {
      const phoneCode = String(this.selectedPhoneCode || '').replace(/\D/g, '')
      if (!phoneCode) return

      const country =
        findCountryByPhoneCode(this.ddiCountries, phoneCode) ||
        findCountryByPhoneCode(this.countries, phoneCode)

      if (country) {
        this.applyPhoneCountry(country)
      } else if (phoneCode === '33') {
        this.applyPhoneCountry({
          iso3: 'FRA',
          country_Name: 'France',
          phone_Code: '33',
        })
      }
    },
    formatInternationalPhoneNumber() {
      return [this.selectedPhoneCode, this.phoneNumber].join('').replace(/\D/g, '')
    },
    makeToast(variant = null) {
      this.$bvToast.toast(this.toastMessage, {
        title: this.$t('common.attention'),
        variant: variant,
        solid: true,
      })
    },
    validateFields() {
      this.$emit('validating')

      this.clearRegistrationErrors()

      let hasErrors = false

      if (this.useInternationalPhone) {
        const internationalPhone = this.formatInternationalPhoneNumber()
        if (!internationalPhone) {
          const warningMsg = this.$t('phone.required')
          this.addRegistrationError({
            msg: warningMsg,
          })
          this.toastMessage = warningMsg
          this.makeToast('warning')
          hasErrors = true
        } else if (!this.validateInternationalPhone(internationalPhone)) {
          const warningMsg = this.$t('cpfAndPhone.phoneInternationalInvalid')
          this.addRegistrationError({
            msg: warningMsg,
          })
          this.toastMessage = warningMsg
          this.makeToast('warning')
          hasErrors = true
        }
      } else if (!this.obj.phone) {
        const warningMsg = this.$t('phone.required')
        this.addRegistrationError({
          msg: warningMsg,
        })
        this.toastMessage = warningMsg
        this.makeToast('warning')
        hasErrors = true
      } else if (!this.validatePhone(this.obj.phone)) {
        const warningMsg = this.$t('phone.invalid')
        this.addRegistrationError({
          msg: warningMsg,
        })
        this.toastMessage = warningMsg
        this.makeToast('warning')

        hasErrors = true
      }

      if (!hasErrors) {
        if (this.useInternationalPhone) {
          this.syncPhoneCountryFromSelection()
          this.obj.phone = this.formatInternationalPhoneNumber()
        }

        this.$store.commit('updateRegistrationObject', {
          ...this.obj,
          phoneCountryIso3: this.obj.phoneCountryIso3 || '',
          phoneCountryName: this.obj.phoneCountryName || '',
          phoneCountryCode: this.obj.phoneCountryCode || '',
          phoneCountryId: this.obj.phoneCountryId || null,
          phoneCountryIsFrance: Boolean(this.obj.phoneCountryIsFrance),
        })

        this.$emit('next')
      }
    },
    validatePhone(phone) {
      return phone.length === 15
    },
    validateInternationalPhone(phone) {
      const digits = String(phone).replace(/\D/g, '')
      return digits.length >= 8 && digits.length <= 15
    },
  },
  watch: {
    phone(newPhone) {
      this.obj.phone = newPhone
    },
    selectedPhoneCode() {
      if (!this.useInternationalPhone) return

      this.syncPhoneCountryFromSelection()
      this.persistPhoneCountry()
    },
    validate(newValue) {
      if (newValue) this.validateFields()
    },
  },
}
</script>

<style>
.phoneNumber {
  display: flex;
  gap: 20px;
}

.inputWidth {
  width: 90px;
}

.ddi-picker {
  position: relative;
  flex-shrink: 0;
  z-index: 5;
}

.ddi-picker--light {
  width: 72px;
  min-width: 72px;
}

.ddi-picker--dark {
  width: 72px;
  min-width: 72px;
}

.ddi-picker-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 4px 2px;
  cursor: pointer;
  text-align: left;
  font-size: inherit;
  line-height: inherit;
}

.ddi-picker-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: inherit;
  line-height: inherit;
}

.ddi-picker-arrow {
  font-size: 0.55rem;
  opacity: 0.75;
  transition: transform 0.15s ease;
  flex-shrink: 0;
}

.ddi-picker-arrow.open {
  transform: rotate(180deg);
}

.ddi-picker-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  min-width: 240px;
  width: max-content;
  max-width: 280px;
  max-height: 180px;
  overflow-y: auto;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
}

.ddi-picker-list--light {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.ddi-picker-list--dark {
  background: #1f1f1f;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
}

.ddi-picker-list li {
  padding: 8px 12px;
  cursor: pointer;
  font-size: var(--font-size-input-secondary);
  line-height: 1.3;
  white-space: nowrap;
}

.ddi-picker-list--light li {
  color: #222;
}

.ddi-picker-list--dark li {
  color: #fff;
}

.ddi-picker-list--light li:hover,
.ddi-picker-list--light li.selected {
  background: rgba(0, 0, 0, 0.06);
}

.ddi-picker-list--dark li:hover,
.ddi-picker-list--dark li.selected {
  background: rgba(255, 255, 255, 0.12);
}

.ddi-picker-loading {
  pointer-events: none;
  opacity: 0.7;
  font-size: 0.8rem;
}
</style>
